// Since we don't have direct access to the ModifiedFinalZSL type, we'll define a compatible interface
interface ZSLTemplate {
  name: string;
  description: string;
  parameters: Array<{
    key: string;
    default: string;
  }>;
  interfaces: any[];
  tables: any[];
  chatbots: any[];
  zaps: any[];
}

interface ValidateZSLSuccessResponse {
  data: {
    valid: boolean;
  };
}

interface ValidateZSLErrorResponse {
  errors: Array<{
    status: number;
    code: string;
    detail: string;
    title?: string;
    source?: string;
    meta?: Record<string, unknown>;
  }>;
}

type ValidateZSLResponse =
  | ValidateZSLSuccessResponse
  | ValidateZSLErrorResponse;

/**
 * Validates ZSL schema by making a request to the validation API
 * @param input The ZSL data to validate
 * @returns An object containing validation result and optional error message
 */
export async function validateZSL(input: ZSLTemplate): Promise<{
  valid: boolean;
  errorMessage?: string;
}> {
  try {
    const response = await fetch(
      "https://zsl-api.zapier.com/api/zsl/v0/templates/validate",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          template: {
            name: input.name,
            description: input.description,
            parameters: input.parameters,
            interfaces: input.interfaces,
            tables: input.tables,
            chatbots: input.chatbots,
            zaps: input.zaps,
            canvases: [],
            edges: [],
            genericAssets: [],
          },
        }),
      },
    );

    const result = (await response.json()) as ValidateZSLResponse;

    // Check for successful validation
    if ("data" in result && result.data && "valid" in result.data) {
      return { valid: result.data.valid };
    } else if ("valid" in result) {
      return { valid: result.valid as boolean };
    } else if ("errors" in result) {
      // Format error messages
      const errorMessage =
        "Validation errors:\n" +
        result.errors
          .map((err) => `- ${err.title || err.code}: ${err.detail}`)
          .join("\n");
      return { valid: false, errorMessage };
    }

    return { valid: false, errorMessage: "Unknown validation response format" };
  } catch (error) {
    return {
      valid: false,
      errorMessage: `Validation request failed: ${
        error instanceof Error ? error.message : String(error)
      }`,
    };
  }
}
